import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/mutations/useCustomCollectionsMutations');

/**
 * Custom Collections mutation hooks using factory pattern
 * Collections + entries CRUD (collection fields managed via custom fields)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customCollectionsApi } from '@/lib/api/custom-collections.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { toast } from 'sonner';
import type {
  CustomCollection,
  CreateCustomCollectionRelationDefinitionDto,
  CreateCustomCollectionDto,
  UpdateCustomCollectionFeaturesDto,
  UpdateCustomCollectionRelationDefinitionDto,
  UpdateCustomCollectionDto,
  CreateCustomCollectionEntryDto,
  UpdateCustomCollectionEntryDto,
} from '@scrambl/shared';
// Collection CRUD using factory
const collectionMutations = createResourceMutations<CustomCollection, CreateCustomCollectionDto, UpdateCustomCollectionDto>({
  resourceName: 'Collection',
  resourceNameSingular: 'collection',
  queryKeyFactory: queryKeys.customCollections,
  api: {
    create: (data: CreateCustomCollectionDto) => customCollectionsApi.createCollection(data),
    update: (id: string, data: UpdateCustomCollectionDto) => customCollectionsApi.updateCollection(id, data),
    delete: (id: string) => customCollectionsApi.deleteCollection(id),
  },
  enableRealtime: false,
});
export const useCreateCustomCollectionMutation = collectionMutations.useCreateMutation;
export const useUpdateCustomCollectionMutation = collectionMutations.useUpdateMutation;
export const useDeleteCustomCollectionMutation = collectionMutations.useDeleteMutation;

/**
 * Bulk delete custom collections mutation hook
 */
export function useBulkDeleteCustomCollectionsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => customCollectionsApi.bulkDeleteCollections(ids),
    onSuccess: (result) => {
      toast.success(`${result.deletedCount} collection${result.deletedCount === 1 ? '' : 's'} deleted`);
      queryClient.invalidateQueries({ queryKey: queryKeys.customCollections.lists() });
    },
    onError: (error) => {
      logger.error('[useBulkDeleteCustomCollectionsMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete collections');
    },
  });
}

// Helper for nested resource mutations with collectionId context
function createNestedMutation<TData, TVariables extends { collectionId: string }>(config: {
  mutationFn: (vars: TVariables) => Promise<TData>;
  getInvalidationKeys: (collectionId: string) => readonly (readonly unknown[] )[];
  successMessage: string;
  errorMessage: string;
  showSuccessToast?: boolean;
}) {
  return function useNestedMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: config.mutationFn,
      onSuccess: (_, variables) => {
        config.getInvalidationKeys(variables.collectionId).forEach(key => {
          queryClient.invalidateQueries({ queryKey: key as unknown[] });
        });
        if (config.showSuccessToast) {
          toast.success(config.successMessage);
        }
      },
      onError: (error) => {
        logger.error(`[${config.errorMessage}]`, error);
        toast.error(error instanceof Error ? error.message : config.errorMessage);
      },
    });
  };
}

// Entry mutations (include dropdown options invalidation)
const entryInvalidationKeys = (id: string) => [
  queryKeys.customCollections.entries(id),
  queryKeys.customCollections.dropdownOptions(id),
];

export const useCreateCustomCollectionEntryMutation = createNestedMutation<
  unknown,
  { collectionId: string; data: CreateCustomCollectionEntryDto }
>({
  mutationFn: ({ collectionId, data }) => customCollectionsApi.createEntry(collectionId, data),
  getInvalidationKeys: entryInvalidationKeys,
  successMessage: 'Entry created successfully',
  errorMessage: 'Failed to create entry',
  showSuccessToast: false,
});

// Custom hook for updating collection entries with optimistic updates
export function useUpdateCustomCollectionEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collectionId, entryId, data }: { collectionId: string; entryId: string; data: UpdateCustomCollectionEntryDto }) =>
      customCollectionsApi.updateEntry(collectionId, entryId, data),
    // Optimistic update: update cache immediately before API call completes
    onMutate: async ({ collectionId, entryId, data }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.customCollections.entries(collectionId) });

      // Snapshot previous values for rollback
      const previousEntries = queryClient.getQueryData(queryKeys.customCollections.entries(collectionId));

      // Always apply the user's own edit immediately. The patch only touches
      // the edited row, so open editors on other rows are unaffected — deferral
      // is reserved for remote (realtime) updates, never the user's own action.
      queryClient.setQueryData(
        queryKeys.customCollections.entries(collectionId),
        (oldData: any) => {
          if (!oldData) return oldData;

          // Handle array format (entries are returned as array)
          if (Array.isArray(oldData)) {
            return oldData.map((entry: any) =>
              entry.id === entryId
                ? { ...entry, ...data }
                : entry
            );
          }

          return oldData;
        }
      );

      // Return context for rollback on error
      return { previousEntries, collectionId };
    },
    // On error, rollback optimistic update
    onError: (error, variables, context) => {
      // Rollback entries cache
      if (context?.previousEntries) {
        queryClient.setQueryData(
          queryKeys.customCollections.entries(context.collectionId),
          context.previousEntries
        );
      }

      logger.error('[useUpdateCustomCollectionEntryMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update entry');
    },
    onSuccess: (updatedEntry, variables) => {
      // The user's own edit always lands immediately. The optimistic value from
      // onMutate is already showing; this swaps in the authoritative server row.
      // Update entry in cache with server response
      queryClient.setQueryData(
        queryKeys.customCollections.entries(variables.collectionId),
        (oldData: any) => {
          if (!oldData) return oldData;

          if (Array.isArray(oldData)) {
            return oldData.map((entry: any) =>
              entry.id === variables.entryId
                ? { ...entry, ...updatedEntry }
                : entry
            );
          }

          return oldData;
        }
      );

      // Also invalidate dropdown options in case the value column changed
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.dropdownOptions(variables.collectionId),
      });
    },
  });
}

export const useDeleteCustomCollectionEntryMutation = createNestedMutation<
  void,
  { collectionId: string; entryId: string }
>({
  mutationFn: ({ collectionId, entryId }) => customCollectionsApi.deleteEntry(collectionId, entryId),
  getInvalidationKeys: entryInvalidationKeys,
  successMessage: 'Entry deleted successfully',
  errorMessage: 'Failed to delete entry',
  showSuccessToast: true,
});

/**
 * Bulk delete collection entries mutation hook
 */
export function useBulkDeleteCustomCollectionEntriesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collectionId, entryIds }: { collectionId: string; entryIds: string[] }) =>
      customCollectionsApi.bulkDeleteEntries(collectionId, entryIds),
    onSuccess: (result, variables) => {
      toast.success(`${result.deletedCount} ${result.deletedCount === 1 ? 'entry' : 'entries'} deleted`);
      queryClient.invalidateQueries({ queryKey: queryKeys.customCollections.entries(variables.collectionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customCollections.dropdownOptions(variables.collectionId) });
    },
    onError: (error) => {
      logger.error('[useBulkDeleteCustomCollectionEntriesMutation] Error:', error);
      toast.error('Failed to delete entries');
    },
  });
}

export function useUpdateCustomCollectionFeaturesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: UpdateCustomCollectionFeaturesDto;
    }) => customCollectionsApi.updateCollectionFeatures(collectionId, data),
    onSuccess: (features, variables) => {
      queryClient.setQueryData(
        queryKeys.customCollections.features(variables.collectionId),
        features,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.customCollections.details() });
      toast.success('Collection features updated');
    },
    onError: error => {
      logger.error('[useUpdateCustomCollectionFeaturesMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update collection features');
    },
  });
}

export function useCreateCustomCollectionRelationshipMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: CreateCustomCollectionRelationDefinitionDto;
    }) => customCollectionsApi.createRelationship(collectionId, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.relationships(variables.collectionId, 'source'),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.relationships(result.targetCollectionId, 'target'),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.customFields.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.entries(variables.collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.entries(result.targetCollectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.details(),
      });
      toast.success('Relationship created');
    },
    onError: error => {
      logger.error('[useCreateCustomCollectionRelationshipMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create relationship');
    },
  });
}

export function useUpdateCustomCollectionRelationshipMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      relationshipId,
      data,
    }: {
      collectionId: string;
      relationshipId: string;
      data: UpdateCustomCollectionRelationDefinitionDto;
    }) => customCollectionsApi.updateRelationship(collectionId, relationshipId, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.relationships(variables.collectionId, 'source'),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.relationships(result.targetCollectionId, 'target'),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.customFields.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.entries(variables.collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.entries(result.targetCollectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.details(),
      });
      toast.success('Relationship updated');
    },
    onError: error => {
      logger.error('[useUpdateCustomCollectionRelationshipMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update relationship');
    },
  });
}

export function useDeleteCustomCollectionRelationshipMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      relationshipId,
    }: {
      collectionId: string;
      relationshipId: string;
    }) => customCollectionsApi.deleteRelationship(collectionId, relationshipId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.relationships(variables.collectionId, 'source'),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.customFields.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.entries(variables.collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customCollections.details(),
      });
      toast.success('Relationship deleted');
    },
    onError: error => {
      logger.error('[useDeleteCustomCollectionRelationshipMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete relationship');
    },
  });
}
